import 'react-color-palette/css';

import { Input } from '@app/features/analytics/components';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  ColorService,
  Hue,
  IColor,
  Saturation,
  useColor,
} from 'react-color-palette';
import { useLayer } from 'react-laag';
import { useDebouncedCallback } from 'use-debounce';

import { DEFAULT_VIDEO_BG_COLOR } from '../../const';
import { ColorPreview } from './color-preview';

type ColorPickerProps = {
  value: string;
  /** Callback when color changes */
  onChange: (color: string) => void;
  /** Optional title for the button */
  title?: string;
  /** Shape of the color swatch */
  shape?: 'circle' | 'rectangle';
  /** Placement of the color picker popup */
  placement?:
    | 'bottom-start'
    | 'bottom-end'
    | 'top-start'
    | 'top-end'
    | 'left-start'
    | 'right-start';
  /** Optional theme colors to display in the picker */
  themeColors?: string[];
  /** When true, the picker cannot be opened and is not interactive */
  disabled?: boolean;
};

/**
 * A generic color picker component.
 * Parent component is responsible for providing the current color value
 * and handling the onChange callback (e.g., dispatching to Redux).
 */
export const ColorPicker = ({
  value,
  title,
  onChange,
  shape = 'circle',
  placement = 'bottom-start',
  themeColors = [],
  disabled = false,
}: ColorPickerProps) => {
  const { t } = useTranslation();

  // Ensure we have a valid hex color
  const validColor =
    typeof value === 'string' && value.startsWith('#')
      ? value
      : DEFAULT_VIDEO_BG_COLOR;

  // useColor provides IColor object for picker UI (Saturation, Hue) and hex for preview
  const [color, setColor] = useColor(validColor);
  const [isOpen, setIsOpen] = useState(false);
  // Track last dispatched color to prevent duplicate dispatches
  const lastDispatchedColorRef = useRef(validColor);

  // Sync color state when value prop changes, but only when picker is closed
  // This prevents race conditions when dragging fast in the picker
  useEffect(() => {
    if (!isOpen) {
      setColor(ColorService.convert('hex', validColor));
      // Update ref to prevent dispatch when syncing from external changes
      lastDispatchedColorRef.current = validColor;
    }
  }, [validColor, setColor, isOpen]);

  const { renderLayer, triggerProps, layerProps } = useLayer({
    isOpen,
    onOutsideClick: () => setIsOpen(false),
    placement,
    triggerOffset: 8,
    auto: true,
  });

  // Debounce the Redux dispatch to reduce re-renders during rapid color changes (dragging)
  const debouncedOnChange = useDebouncedCallback((newHex: string) => {
    // Skip if color hasn't actually changed (prevents duplicate dispatches)
    if (lastDispatchedColorRef.current.toLowerCase() === newHex.toLowerCase()) {
      return;
    }
    lastDispatchedColorRef.current = newHex;
    onChange(newHex);
  }, 50);

  const handleColorChange = useCallback(
    (newColor: IColor) => {
      // Update local state immediately for smooth picker feedback
      // (avoids lag from Redux round-trip while dragging)
      setColor(newColor);
      // Notify parent with debounced dispatch to reduce Redux updates
      debouncedOnChange(newColor.hex);
    },
    [debouncedOnChange, setColor],
  );

  const handleButtonClick = useCallback(
    (e: React.MouseEvent) => {
      // Stop propagation to prevent parent button clicks (e.g., blur toggle)
      e.stopPropagation();
      setIsOpen(!isOpen);
    },
    [isOpen],
  );

  return (
    <>
      <button
        {...triggerProps}
        onClick={handleButtonClick}
        disabled={disabled}
        className="!border-none bg-transparent !outline-none !ring-0 focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0 active:!outline-none active:!ring-0 hover:!outline-none hover:!ring-0 disabled:cursor-not-allowed disabled:opacity-60"
        title={title}
      >
        <ColorPreview backgroundColor={color.hex} shape={shape} />
      </button>
      {isOpen &&
        renderLayer(
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            {...layerProps}
            onClick={e => e.stopPropagation()}
            className="z-50 w-64 rounded-md border border-solid border-gray-300 bg-white shadow-lg"
          >
            <div className="relative [&_.rcp-hue]:rounded-none [&_.rcp-saturation]:rounded-t-md">
              <Saturation
                height={200}
                color={color}
                onChange={handleColorChange}
              />
              <Hue color={color} onChange={handleColorChange} />
              <HexInput color={color} onChange={handleColorChange} />
            </div>
            {themeColors.length > 0 && (
              <div className="p-4">
                <p className="mb-2 text-left text-sm font-medium text-gray-900">
                  {t('content-recording.appearance.color-picker.theme-colors')}
                </p>
                <div className="grid grid-cols-9 gap-2">
                  {themeColors.map((hexColor, index) => (
                    <ThemeColorButton
                      key={`${hexColor}-${index}`}
                      color={hexColor}
                      onClick={handleColorChange}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>,
        )}
    </>
  );
};

const HexInput = ({
  color,
  onChange,
}: {
  color: IColor;
  onChange: (color: IColor) => void;
}) => (
  <Input
    hideLabel
    name="hex"
    value={color.hex}
    onChange={e =>
      onChange(
        ColorService.convert('hex', (e.target as HTMLInputElement).value),
      )
    }
    className="mx-4 mt-4"
  />
);

const ThemeColorButton = memo(function ThemeColorButton({
  color: hex,
  onClick,
}: {
  color: string;
  onClick: (color: IColor) => void;
}) {
  const [color] = useColor(hex);
  return (
    <button onClick={() => onClick(color)} title={hex}>
      <ColorPreview backgroundColor={color.hex} size="small" />
    </button>
  );
});
